import { useState } from "react";
import { Box, TextField, Typography, SxProps } from "@mui/material";
import EditFieldModal from "@/modules/common/components/modals/edit-field-modal";
import CustomLabel from "@/modules/common/components/input/custom-label";
import { useResolveRating } from "@/modules/lead-page/new/hooks/ratings/resolve-rating.hook";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";

interface ResolveRatingModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  ratingUId: string;
  onSuccess?: () => void;
  modalSx?: SxProps;
}

const ResolveRatingModal: React.FC<ResolveRatingModalProps> = ({
  open,
  setOpen,
  ratingUId,
  onSuccess,
  modalSx,
}) => {
  const [comment, setComment] = useState("");

  const { isMobile } = useScreenSize();
  const { handleResolveRating, isPending } = useResolveRating({
    ratingUId,
    onSuccess: () => {
      setComment("");
      setOpen(false);
      onSuccess?.();
    },
  });

  const handleCloseModal = () => {
    setComment("");
    setOpen(false);
  };

  const handleConfirm = () => {
    handleResolveRating(comment.trim() || undefined);
  };

  return (
    <EditFieldModal
      open={open}
      setOpen={setOpen}
      modalSx={modalSx}
      header={
        <Typography
          sx={{
            ...nawyTheme.nawy.typography.english.variants.body.lg.semibold,
            color: nawyTheme.nawy.colors.semantic.text.primary,
          }}
        >
          Resolve issue
        </Typography>
      }
      width={480}
      padding={`${nawyTheme.nawy.spacing.sm} ${nawyTheme.nawy.spacing.md}`}
      handleConfirm={handleConfirm}
      handleCloseModal={handleCloseModal}
      isLoading={isPending}
      confirmText="Resolve review"
      cancelText="Cancel"
      cancelButtonSx={{
        borderRadius: nawyTheme.nawy.radius.lg,
        backgroundColor: nawyTheme.nawy.colors.semantic.layer.primary,
        color: nawyTheme.nawy.colors.semantic.text.accent,
        padding: `${nawyTheme.nawy.spacing.xs} ${nawyTheme.nawy.spacing.md}`,
        fontSize:
          nawyTheme.nawy.typography.english.variants.body.md.semibold.fontSize,
        fontWeight:
          nawyTheme.nawy.typography.english.variants.body.sm.semibold
            .fontWeight,
        border: `1px solid ${nawyTheme.nawy.colors.semantic.layer.accent}`,
        boxShadow: "none",
        minWidth: "140px",
        "&:hover": {
          backgroundColor: nawyTheme.nawy.colors.semantic.state.onColor08,
        },
        "&:active": {
          backgroundColor: nawyTheme.nawy.colors.semantic.state.onColor12,
        },
      }}
      applyButtonSx={{
        borderRadius: nawyTheme.nawy.radius.lg,
        backgroundColor: nawyTheme.nawy.colors.semantic.layer.accent,
        color: nawyTheme.nawy.colors.semantic.text.onColor,
        padding: `${nawyTheme.nawy.spacing.xs} ${nawyTheme.nawy.spacing.md}`,
        fontSize:
          nawyTheme.nawy.typography.english.variants.body.md.semibold.fontSize,
        fontWeight:
          nawyTheme.nawy.typography.english.variants.body.sm.semibold
            .fontWeight,
        boxShadow: "none",
        minWidth: "140px",
        "&:hover": {
          background: nawyTheme.nawy.colors.semantic.state.light08,
          color: nawyTheme.nawy.colors.semantic.text.accent,
        },
        border: `1px solid ${nawyTheme.nawy.colors.semantic.layer.accent}`,
        "&:active": {
          background: nawyTheme.nawy.colors.semantic.state.light16,
        },
        ...(isMobile
          ? {
              "&:disabled": {
                height: "42px",
                opacity: 1,
                backgroundColor:
                  nawyTheme.nawy.colors.semantic.layer.accent,
                color: nawyTheme.nawy.colors.semantic.text.onColor,
                border: `1px solid ${nawyTheme.nawy.colors.semantic.layer.accent}`,
              },
            }
          : {}),
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <CustomLabel
          sx={{
            fontSize:
              nawyTheme.nawy.typography.english.variants.body.sm.regular
                .fontSize,
            fontWeight:
              nawyTheme.nawy.typography.english.variants.body.xs.regular
                .fontWeight,
            lineHeight:
              nawyTheme.nawy.typography.english.variants.body.xs.regular
                .lineHeight,
            color: nawyTheme.nawy.colors.semantic.text.secondary,
          }}
        >
          Comment (optional)
        </CustomLabel>
        <TextField
          placeholder="Placeholder"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          multiline
          rows={4}
          fullWidth
          variant="outlined"
          sx={{
            border: `1px solid ${nawyTheme.nawy.colors.semantic.layer.primary}`,
            marginBottom: 0,
            "& .MuiOutlinedInput-root": {
              borderRadius: nawyTheme.nawy.radius.lg,
              backgroundColor: nawyTheme.nawy.colors.semantic.layer.primary,
              padding: `${nawyTheme.nawy.spacing.xs} ${nawyTheme.nawy.spacing.sm}`,
            },
            "& .MuiInputBase-input": {
              padding: `${nawyTheme.nawy.spacing.xxs} ${nawyTheme.nawy.spacing.xxs}`,
            },
            "& .MuiInputBase-input::placeholder": {
              color: nawyTheme.nawy.colors.semantic.text.secondary,
              fontSize:
                nawyTheme.nawy.typography.english.variants.body.md.regular
                  .fontSize,
              opacity: 1,
            },
          }}
        />
      </Box>
    </EditFieldModal>
  );
};

export default ResolveRatingModal;
